import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { API_URL } from '../../../api/api';
import {
  FiClock, FiFilter, FiDownload, FiRefreshCw,
  FiEye, FiUser, FiDatabase, FiActivity, FiBarChart2,
  FiSearch, FiCalendar, FiChevronDown, FiChevronUp,
  FiFileText, FiArchive, FiEdit, FiTrash2, FiPlus,
  FiCheck, FiX, FiGlobe, FiMonitor, FiSmartphone,
  FiArrowUpRight, FiArrowDownRight, FiChevronRight,
  FiList, FiPieChart, FiTrendingUp, FiUsers, FiHash
} from 'react-icons/fi';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';


const token = localStorage.getItem('token');

const AuditHistoryLayer = () => {
  // State management
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [events, setEvents] = useState([]);

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    event: '',
    user_id: '',
    start_date: null,
    end_date: null
  });

  // Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    per_page: 15
  });

  // Expanded rows for details
  const [expandedRows, setExpandedRows] = useState({});

  // Show/hide details
  const [showDetails, setShowDetails] = useState({});

  // Initial fetch
  useEffect(() => {
    fetchAuditLogs();
    fetchStatistics();
    fetchEvents();
  }, []);

  // Fetch audit logs with filters and pagination
  const fetchAuditLogs = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pagination.per_page,
        ...filters
      };

      // Add date filters if set
      if (filters.start_date) {
        params.start_date = filters.start_date.toISOString().split('T')[0];
      }
      if (filters.end_date) {
        params.end_date = filters.end_date.toISOString().split('T')[0];
      }

      console.log('Fetching audit logs with params:', params);

      const response = await axios.get(`${API_URL}/audits`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Audit logs response:', response.data);

      setAuditLogs(response.data.data || []);
      setPagination({
        current_page: response.data.current_page || 1,
        total_pages: response.data.last_page || 1,
        total_items: response.data.total || 0,
        per_page: response.data.per_page || 15
      });
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      alert('Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API_URL}/audits/statistics/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(response.data.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Fetch events for filter dropdown
  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/audits/statistics/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters
  const applyFilters = () => {
    fetchAuditLogs(1);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      event: '',
      user_id: '',
      start_date: null,
      end_date: null
    });
    fetchAuditLogs(1);
  };

  // Export to CSV
  const exportToCSV = async () => {
    try {
      const params = {};
      if (filters.start_date) {
        params.start_date = filters.start_date.toISOString().split('T')[0];
      }
      if (filters.end_date) {
        params.end_date = filters.end_date.toISOString().split('T')[0];
      }

      const response = await axios.post(`${API_URL}/audits/export`, params, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      alert('Audit logs exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export audit logs.');
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Toggle details visibility
  const toggleDetails = (id) => {
    setShowDetails(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Format JSON for display
  const formatJSON = (obj) => {
    if (!obj || Object.keys(obj).length === 0) return '{}';
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  // Get event badge color
  const getEventBadge = (event) => {
    const colors = {
      created: 'success',
      updated: 'warning',
      deleted: 'danger',
      restored: 'info',
      retrieved: 'primary',
      saved: 'secondary'
    };

    const color = colors[event] || 'secondary';
    const displayNames = {
      created: 'Created',
      updated: 'Updated',
      deleted: 'Deleted',
      restored: 'Restored',
      retrieved: 'Viewed',
      saved: 'Saved'
    };

    return (
      <span className={`badge bg-${color}`}>
        {displayNames[event] || event}
      </span>
    );
  };

  // Get model type display name
  const getModelTypeDisplay = (type) => {
    if (!type) return 'Unknown';
    const parts = type.split('\\');
    return parts[parts.length - 1];
  };

  // Parse user agent
  const parseUserAgent = (userAgent) => {
    if (!userAgent) return { browser: 'Unknown', device: 'Unknown' };

    let browser = 'Unknown';
    let device = 'Desktop';

    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    else if (userAgent.includes('Opera')) browser = 'Opera';

    if (userAgent.includes('Mobile')) device = 'Mobile';
    else if (userAgent.includes('Tablet')) device = 'Tablet';

    return { browser, device };
  };

  // Render value comparison
  const renderValueComparison = (oldValues, newValues, event) => {
    if (event === 'created') {
      return (
        <div className="text-success">
          <small>New record created with values:</small>
          <pre className="mb-0 mt-1 bg-light p-2 rounded" style={{ fontSize: '12px' }}>
            {formatJSON(newValues)}
          </pre>
        </div>
      );
    }

    if (event === 'deleted') {
      return (
        <div className="text-danger">
          <small>Record deleted with values:</small>
          <pre className="mb-0 mt-1 bg-light p-2 rounded" style={{ fontSize: '12px' }}>
            {formatJSON(oldValues)}
          </pre>
        </div>
      );
    }

    if (event === 'updated') {
      const changes = [];
      if (oldValues && newValues) {
        Object.keys(newValues).forEach(key => {
          const oldVal = oldValues[key];
          const newVal = newValues[key];
          if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            changes.push({ key, old: oldVal, new: newVal });
          }
        });
      }

      if (changes.length === 0) {
        return <span className="text-muted">No changes detected</span>;
      }

      return (
        <div>
          <small className="d-block mb-1">Changes:</small>
          <div className="changes-container">
            {changes.slice(0, 3).map((change, index) => (
              <div key={index} className="mb-1">
                <span className="badge bg-light text-dark me-1">{change.key}</span>
                <span className="text-danger">
                  {typeof change.old === 'object' ? JSON.stringify(change.old) : String(change.old) || 'null'}
                </span>
                <FiArrowRight className="mx-1" />
                <span className="text-success">
                  {typeof change.new === 'object' ? JSON.stringify(change.new) : String(change.new) || 'null'}
                </span>
              </div>
            ))}
            {changes.length > 3 && (
              <small className="text-muted">+{changes.length - 3} more changes</small>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  // Clear old logs (admin only)
  const clearOldLogs = async () => {
    if (!window.confirm('Are you sure you want to clear old audit logs? This action cannot be undone.')) {
      return;
    }

    const days = prompt('Delete logs older than how many days?', '90');
    if (!days || isNaN(days) || parseInt(days) < 1) {
      alert('Please enter a valid number of days.');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/audits/clear`, {
        days: parseInt(days)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(response.data.message);
      fetchAuditLogs();
      fetchStatistics();
    } catch (error) {
      console.error('Error clearing old logs:', error);
      alert('Failed to clear old logs. You may not have admin privileges.');
    }
  };

  // Prepare chart data
  const eventChartData = statistics?.events?.map(item => ({
    name: item.event,
    value: item.count
  })) || [];

  const activityChartData = statistics?.activity_by_date?.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    count: item.count
  })).reverse() || [];

  // Loading state
  if (loading && auditLogs.length === 0) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading audit history...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-gradient-primary text-white py-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">
                    <FiClock className="me-2" />
                    Audit History
                  </h5>
                  <p className="mb-0 opacity-75 small">
                    Track all system activities and changes
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <button
                    onClick={exportToCSV}
                    className="btn btn-light btn-sm"
                    title="Export to CSV"
                  >
                    <FiDownload className="me-1" /> Export
                  </button>
                  <button
                    onClick={() => fetchAuditLogs()}
                    className="btn btn-light btn-sm"
                    title="Refresh"
                  >
                    <FiRefreshCw />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="row mb-4">
          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm bg-gradient-primary text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1 opacity-75">
                      <FiDatabase className="me-1" />
                      Total Audits
                    </p>
                    <p className="mb-0">{statistics.total_audits?.toLocaleString() || 0}</p>
                  </div>
                  <FiActivity size={22} />
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm bg-gradient-info text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1 opacity-75">
                      <FiList className="me-1" />
                      Event Types
                    </p>
                    <p className="mb-0">{statistics.events?.length || 0}</p>
                  </div>
                  <FiBarChart2 size={32} />
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm bg-gradient-success text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1 opacity-75">
                      <FiUsers className="me-1" />
                      Active Users
                    </p>
                    <p className="mb-0">{statistics.top_users?.length || 0}</p>
                  </div>
                  <FiUsers size={32} />
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card border-0 shadow-sm bg-gradient-warning text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="mb-1 opacity-75">
                      <FiTrendingUp className="me-1" />
                      Recent Activity
                    </p>
                    <p className="mb-0">
                      {statistics.activity_by_date?.[0]?.count || 0}
                    </p>
                    <small className="opacity-75">Today</small>
                  </div>
                  <FiTrendingUp size={32} />
                </div>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="col-lg-8 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <FiActivity className="me-2" />
                  Activity Trend (Last 30 Days)
                </h6>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={activityChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#0088FE"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="col-lg-4 mb-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-light">
                <h6 className="mb-0">
                  <FiPieChart className="me-2" />
                  Event Distribution
                </h6>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={eventChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {eventChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Card */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-light">
              <h6 className="mb-0">
                <FiFilter className="me-2" />
                Filters
              </h6>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Event Type</label>
                  <select
                    className="form-select"
                    value={filters.event}
                    onChange={(e) => handleFilterChange('event', e.target.value)}
                  >
                    <option value="">All Events</option>
                    {events.map(event => (
                      <option key={event} value={event}>
                        {event.charAt(0).toUpperCase() + event.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Start Date</label>
                  <DatePicker
                    selected={filters.start_date}
                    onChange={(date) => handleFilterChange('start_date', date)}
                    className="form-control"
                    placeholderText="Select start date"
                    maxDate={new Date()}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">End Date</label>
                  <DatePicker
                    selected={filters.end_date}
                    onChange={(date) => handleFilterChange('end_date', date)}
                    className="form-control"
                    placeholderText="Select end date"
                    maxDate={new Date()}
                    minDate={filters.start_date}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search in logs..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>

                <div className="col-12">
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      onClick={applyFilters}
                      className="btn btn-primary"
                    >
                      <FiFilter className="me-1" />
                      Apply Filters
                    </button>
                    <button
                      onClick={resetFilters}
                      className="btn btn-outline-secondary"
                    >
                      Reset
                    </button>
                    <button
                      onClick={clearOldLogs}
                      className="btn btn-outline-danger"
                      title="Clear old logs (Admin only)"
                    >
                      <FiTrash2 className="me-1" />
                      Clear Old
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <FiClock className="me-2" />
                Audit Logs ({pagination.total_items} records)
              </h6>
              <div className="d-flex gap-2 align-items-center">
                <small className="text-muted">
                  Page {pagination.current_page} of {pagination.total_pages}
                </small>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}></th>
                      <th style={{ width: '180px' }}>Timestamp</th>
                      <th>User</th>
                      <th>Event</th>
                      <th>Model</th>
                      <th>IP Address</th>
                      <th>Changes</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          <FiClock size={48} className="text-muted mb-3" />
                          <h5 className="text-muted">No audit logs found</h5>
                          <p className="text-muted">Try adjusting your filters</p>
                        </td>
                      </tr>
                    ) : (
                      auditLogs.map((log) => (
                        <React.Fragment key={log.id}>
                          <tr>
                            <td>
                              <button
                                onClick={() => toggleRowExpansion(log.id)}
                                className="btn btn-sm btn-outline-primary"
                              >
                                {expandedRows[log.id] ? <FiChevronUp /> : <FiChevronDown />}
                              </button>
                            </td>
                            <td>
                              <div className="text-nowrap">
                                {formatDate(log.created_at)}
                              </div>
                              <small className="text-muted d-block">
                                {log.created_at_human || new Date(log.created_at).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                {log.user?.image ? (
                                  <img
                                    src={log.user.image}
                                    alt={log.user.name}
                                    className="rounded-circle me-2"
                                    style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2"
                                    style={{ width: '32px', height: '32px' }}>
                                    <FiUser />
                                  </div>
                                )}
                                <div>
                                  <div className="fw-semibold">
                                    {log.user?.name || 'System'}
                                  </div>
                                  <small className="text-muted">
                                    {log.user?.email || 'N/A'}
                                  </small>
                                </div>
                              </div>
                            </td>
                            <td>
                              {getEventBadge(log.event)}
                            </td>
                            <td>
                              <div>
                                <span className="fw-semibold">
                                  {getModelTypeDisplay(log.auditable_type)}
                                </span>
                                <small className="text-muted d-block">
                                  ID: {log.auditable_id}
                                </small>
                              </div>
                            </td>
                            <td>
                              <div>
                                <code>{log.ip_address}</code>
                                {log.user_agent && (
                                  <small className="text-muted d-block">
                                    {parseUserAgent(log.user_agent).browser} • {parseUserAgent(log.user_agent).device}
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="text-truncate" style={{ maxWidth: '200px' }}>
                                {log.event === 'created' && (
                                  <span className="text-success">
                                    <FiPlus className="me-1" />
                                    Created
                                  </span>
                                )}
                                {log.event === 'updated' && (
                                  <span className="text-warning">
                                    <FiEdit className="me-1" />
                                    Updated
                                  </span>
                                )}
                                {log.event === 'deleted' && (
                                  <span className="text-danger">
                                    <FiTrash2 className="me-1" />
                                    Deleted
                                  </span>
                                )}
                                {log.event === 'restored' && (
                                  <span className="text-info">
                                    <FiArchive className="me-1" />
                                    Restored
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button
                                  onClick={() => toggleDetails(log.id)}
                                  className="btn btn-outline-info"
                                  title="View Details"
                                >
                                  <FiEye />
                                </button>
                                <button
                                  onClick={() => alert('Coming soon: View full details')}
                                  className="btn btn-outline-primary"
                                  title="More Info"
                                >
                                  <FiChevronRight />
                                </button>
                              </div>
                            </td>
                          </tr>

                          {/* Expanded row with details */}
                          {expandedRows[log.id] && (
                            <tr>
                              <td colSpan="8" className="bg-light">
                                <div className="p-3">
                                  <div className="row">
                                    <div className="col-md-6">
                                      <h6 className="mb-3">Old Values</h6>
                                      <pre className="bg-white p-3 rounded border" style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                        {formatJSON(log.old_values)}
                                      </pre>
                                    </div>
                                    <div className="col-md-6">
                                      <h6 className="mb-3">New Values</h6>
                                      <pre className="bg-white p-3 rounded border" style={{ fontSize: '12px', maxHeight: '200px', overflow: 'auto' }}>
                                        {formatJSON(log.new_values)}
                                      </pre>
                                    </div>
                                    <div className="col-12 mt-3">
                                      <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                          <small className="text-muted">
                                            URL: {log.url || 'N/A'}
                                          </small>
                                        </div>
                                        <div>
                                          <small className="text-muted">
                                            User Agent: {log.user_agent ? log.user_agent.substring(0, 50) + '...' : 'N/A'}
                                          </small>
                                        </div>
                                        <div>
                                          <small className="text-muted">
                                            Tags: {log.tags || 'N/A'}
                                          </small>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}

                          {/* Show details modal alternative */}
                          {showDetails[log.id] && (
                            <tr>
                              <td colSpan="8" className="bg-light">
                                <div className="p-3">
                                  <h6 className="mb-3">Value Changes</h6>
                                  {renderValueComparison(log.old_values, log.new_values, log.event)}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <nav className="d-flex justify-content-center mt-4">
                  <ul className="pagination">
                    <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => fetchAuditLogs(pagination.current_page - 1)}
                        disabled={pagination.current_page === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {[...Array(Math.min(5, pagination.total_pages)).keys()].map((num) => {
                      const pageNum = Math.max(1, Math.min(
                        pagination.current_page - 2,
                        pagination.total_pages - 4
                      )) + num;

                      if (pageNum > 0 && pageNum <= pagination.total_pages) {
                        return (
                          <li key={pageNum} className={`page-item ${pagination.current_page === pageNum ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => fetchAuditLogs(pageNum)}
                            >
                              {pageNum}
                            </button>
                          </li>
                        );
                      }
                      return null;
                    }).filter(Boolean)}

                    <li className={`page-item ${pagination.current_page === pagination.total_pages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => fetchAuditLogs(pagination.current_page + 1)}
                        disabled={pagination.current_page === pagination.total_pages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </div>
            <div className="card-footer bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Showing {auditLogs.length} of {pagination.total_items} records
                </small>
                <small className="text-muted">
                  {pagination.per_page} per page
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Chart colors constant
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

// FiArrowRight icon component (if not imported)
const FiArrowRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-arrow-right">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

// FiTablet icon (if not imported)
const FiTablet = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-tablet">
    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
    <line x1="12" y1="18" x2="12" y2="18"></line>
  </svg>
);

export default AuditHistoryLayer;